import { toWSDateString } from "@wingspanhq/fe-component-library";
import {
  PayeeRequirement,
  RequirementStatus
} from "@wingspanhq/wingspan-api-sdk";

import { useModalShareTaxInformationWithPayers } from "../../../../../shared/components/ModalShareTaxInformationWithPayers/useModalShareTaxInformationWithPayers";
import { getRequirementDefinitionTableCellProps } from "../../../../RequirementDefinitions/utils";
import { CustomRequirement, RequirementStatusViewProps } from "../../../types";
import { getTaxVerificationRequirementName } from "../../TaxVerification/configs/helpers/getTaxVerificationRequirementName";
import { getClientsCellProps } from "../helpers/getClientsCellProps";

export function useGetTaxVerificationCustomRequirement(): (
  requirements: PayeeRequirement[]
) => CustomRequirement {
  const modalFormW9Consent = useModalShareTaxInformationWithPayers();

  return requirements => {
    const baseRequirement = requirements[0];

    const completedTaxVerificationRequirements = requirements.filter(
      requirement =>
        requirement.status === RequirementStatus.COMPLETED ||
        requirement.dataSourceMetadata?.verificationStatus === "Verified"
    );

    const consentsSharedRequirements = requirements.filter(
      requirement =>
        // `dataSourceMetadata` is an untyped backend-owned blob (no SDK schema);
        // `shareTaxDocument` is compared against its raw wire value.
        requirement.dataSourceMetadata?.payeeOwnedData?.shareTaxDocument ===
        "Allow"
    );

    const status =
      completedTaxVerificationRequirements.length === requirements.length
        ? RequirementStatus.COMPLETED
        : RequirementStatus.PENDING_COMPLETION;

    // `updatedAt` no longer exists on the instance (v3) — `events.createdAt` is
    // the closest available recency signal (accepted breakage, see remodel spec §1).
    const latestUpdatedRequirement = requirements.reduce((latest, current) => {
      const currentCreatedAt = current.events?.createdAt?.getTime() ?? 0;
      const latestCreatedAt = latest.events?.createdAt?.getTime() ?? 0;
      return currentCreatedAt > latestCreatedAt ? current : latest;
    });

    const typeCell = {
      ...getRequirementDefinitionTableCellProps({
        type: baseRequirement.type!
      }),
      secondaryText: `Updated ${toWSDateString(
        latestUpdatedRequirement.events?.createdAt,
        "input"
      )}`
    };

    return {
      payerAccountId: baseRequirement.payerAccountId!,
      status,
      clientsCell: getClientsCellProps(requirements),
      nameCell: {
        text: getTaxVerificationRequirementName(baseRequirement)
      },
      typeCell,
      engagementsCell: {},
      statusCell: {
        ...getStatusCellProps(
          completedTaxVerificationRequirements.length,
          requirements.length
        ),
        secondaryText: `Shared with ${consentsSharedRequirements.length} of ${requirements.length} clients`
      },
      menuActions:
        consentsSharedRequirements.length !== requirements.length
          ? [
              {
                label: "Consent to share information",
                icon: "clipboard-check",
                onClick: () => modalFormW9Consent.open()
              }
            ]
          : []
    };
  };
}

const getStatusCellProps = (
  completedRequirementsCount: number,
  totalRequirementsCount: number
): RequirementStatusViewProps => {
  if (completedRequirementsCount === totalRequirementsCount) {
    return {
      pill: {
        text: "Complete",
        theme: "success",
        icon: true
      }
    };
  } else if (completedRequirementsCount === 0) {
    return {
      pill: {
        theme: "warning",
        text: "Action required",
        icon: true
      }
    };
  } else {
    return {
      pill: {
        theme: "warning",
        text: "Partially completed",
        icon: true
      }
    };
  }
};
